import { useMemo, useRef } from 'react';
import type {
  FormFieldOption,
  ProcessedFormField,
  ResolvedOption,
  ScopeBundle,
} from '@/types/api/formSchema';
import { evaluateExpression, buildEvalScope, isJexlString } from '@/lib/formula';

// Hoisted so we don't reallocate a Set for every field on every recompute.
const OPTION_TYPES = new Set(['select', 'radio', 'checkbox']);

type ResolvedField = ProcessedFormField & {
  required: boolean;
  hidden: boolean;
  disabled: boolean;
  resolvedOptions?: ResolvedOption[];
};

type UseFormulaFieldsResult = {
  resolvedFields: ResolvedField[];
  computedValues: Record<string, unknown>;
  visibleFields: ResolvedField[];
  readOnlyFieldNames: Set<string>;
};

export function useFormulaFields(
  fields: ProcessedFormField[] | undefined,
  scope: ScopeBundle | undefined,
  formValues: Record<string, unknown>
): UseFormulaFieldsResult {
  // RHF v7's form.watch() returns a reference-stable Proxy — useMemo's
  // Object.is check sees the same ref and skips recomputation. Shallow-
  // compare against the previous snapshot so the downstream memo re-runs
  // only when a value actually changed, without the O(n) JSON.stringify
  // that would otherwise run on every render.
  // Tested against react-hook-form v7.x — re-verify form.watch() reference
  // semantics if upgrading to v8+.
  const prevRef = useRef<Record<string, unknown>>({});
  const snapshot = { ...formValues };
  const prev = prevRef.current;
  const snapKeys = Object.keys(snapshot);
  if (
    snapKeys.length !== Object.keys(prev).length ||
    snapKeys.some((k) => !Object.is(snapshot[k], prev[k]))
  ) {
    prevRef.current = snapshot;
  }
  const stableFormValues = prevRef.current;

  return useMemo(() => {
    if (!fields?.length) {
      return {
        resolvedFields: [],
        computedValues: {},
        visibleFields: [],
        readOnlyFieldNames: new Set<string>(),
      };
    }

    const evalScope = buildEvalScope(scope, stableFormValues);
    const computedValues: Record<string, unknown> = {};
    const readOnlyFieldNames = new Set<string>();

    const resolvedFields: ResolvedField[] = fields.map((field) => {
      const hidden = isJexlString(field.hidden)
        ? evaluateExpression<boolean>(field.hidden, evalScope, false)
        : field.hidden === true;

      const required = isJexlString(field.required)
        ? evaluateExpression<boolean>(field.required, evalScope, false)
        : field.required === true;

      const disabled = isJexlString(field.disabled)
        ? evaluateExpression<boolean>(field.disabled, evalScope, false)
        : field.disabled === true;

      // Always record the result (even undefined) so the sync effect can reset
      // the field when a dependency is cleared and the expression can't resolve.
      if (typeof field.value === 'string' && !field.prefilled) {
        computedValues[field.name] = evaluateExpression<unknown>(field.value, evalScope, undefined);
      }

      const isComputed = typeof field.value === 'string' && !field.prefilled;
      if (isComputed && disabled) {
        readOnlyFieldNames.add(field.name);
      }

      // ADR-0070 §5.2.1: resolve per-option hidden/disabled for select/radio/checkbox.
      // `field.fields` is overloaded (see TaskerFormField): option list for
      // select/radio/checkbox, or nested schema for array-type fields. Only
      // read it as an option list when the parent type actually holds options —
      // otherwise nested TaskerFormField[] would be misread as FormFieldOption[].
      let resolvedOptions: ResolvedOption[] | undefined;
      const rawList =
        field.list ||
        field.options ||
        (OPTION_TYPES.has(field.type) ? field.fields : undefined);
      if (rawList && Array.isArray(rawList)) {
        const hasPerOptionFlags = rawList.some(
          (opt) =>
            typeof opt !== 'string' && (opt.hidden !== undefined || opt.disabled !== undefined)
        );
        if (hasPerOptionFlags) {
          resolvedOptions = (rawList as FormFieldOption[])
            .filter((opt) => {
              if (typeof opt === 'string') return true;
              if (isJexlString(opt.hidden))
                return !evaluateExpression<boolean>(opt.hidden, evalScope, false);
              return opt.hidden !== true;
            })
            .map((opt) => {
              if (typeof opt === 'string') return { name: opt, display_name: opt, disabled: false };
              const optDisabled = isJexlString(opt.disabled)
                ? evaluateExpression<boolean>(opt.disabled, evalScope, false)
                : opt.disabled === true;
              return {
                name: opt.name,
                display_name: opt.display_name || opt.name,
                disabled: optDisabled,
              };
            });
        }
      }

      return { ...field, hidden, required, disabled, resolvedOptions };
    });

    const visibleFields = resolvedFields.filter((f) => !f.hidden);

    return { resolvedFields, computedValues, visibleFields, readOnlyFieldNames };
  }, [fields, scope, stableFormValues]);
}
